import { useState } from 'react';

import LoaderButton from 'components/Button/LoaderButton';
import { RegisterImplementationProps } from 'constants/types';
import {
  AuthorizeContextValue,
  withAuthorizeContext,
} from 'pages/Authorize/AuthorizeContext';
import { AnalyticsEventName, track } from 'utils/analytics';
import { getClientDisplayNameInSentence } from 'utils/connect-branding';
import { getBackendUrl } from '../../../../services/get-backend-url';
import { useOAuthStatePolling } from '../../OAuth/useOAuthStatePolling';
import SignInBase from '../SignInBase';
import { getProviderFormLabel } from '../utils';

const MARKETPLACE_REQUEST_TIMEOUT_MS = 900_000;
const MARKETPLACE_REQUEST_TIMEOUT_MESSAGE =
  "We didn't hear back from Paylocity in time. Try again and submit the request in the Paylocity window.";

export const marketplaceListingName = (clientName: string) =>
  `${clientName} via Finch`;

export const getMarketplaceStartUrl = ({
  backendUrl,
  sessionKey,
}: {
  backendUrl: string;
  sessionKey: string;
}) => `${backendUrl}/idp/start/paylocity/${encodeURIComponent(sessionKey)}`;

export const SignInPaylocityOAuthBase = ({
  client,
  provider,
  onSubmit,
  onMockOAuth,
  idpRedirect,
  sessionKey,
  setError,
}: RegisterImplementationProps &
  Pick<AuthorizeContextValue, 'idpRedirect' | 'sessionKey' | 'setError'>) => {
  const [buttonLoading, setButtonLoading] = useState(false);

  const { loading, openOAuthWindow } = useOAuthStatePolling({
    sessionKey,
    onCodeReceived: async (clientCode) => {
      await onSubmit({ clientCode });
    },
    setError,
    timeoutMs: MARKETPLACE_REQUEST_TIMEOUT_MS,
    onTimeout: () =>
      setError({ message: MARKETPLACE_REQUEST_TIMEOUT_MESSAGE, status: null }),
  });

  const onIdpRedirectClick = async () => {
    setButtonLoading(true);
    try {
      await onSubmit({ idpRedirect });
    } finally {
      setButtonLoading(false);
    }
  };

  const onMarketplaceClick = async () => {
    setButtonLoading(true);
    try {
      if (await onMockOAuth()) {
        return;
      }

      const startUrl = getMarketplaceStartUrl({
        backendUrl: getBackendUrl(),
        sessionKey,
      });

      track(AnalyticsEventName.RedirectedToOAuthUrl, {
        oauthUrl: startUrl,
      });

      openOAuthWindow(startUrl);
    } finally {
      setButtonLoading(false);
    }
  };

  if (idpRedirect) {
    return (
      <SignInBase
        formLabel={getProviderFormLabel(provider)}
        instructions={
          <p>
            Connect your Paylocity account to{' '}
            {getClientDisplayNameInSentence(client)}. Click Connect to finish
            setting up the integration.
          </p>
        }
      >
        <div
          style={{
            marginTop: 'auto',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <LoaderButton
            isLoading={buttonLoading}
            disabled={buttonLoading}
            onClick={onIdpRedirectClick}
            text="Connect"
          />
        </div>
      </SignInBase>
    );
  }

  return (
    <SignInBase
      formLabel={getProviderFormLabel(provider)}
      spacing="md"
      instructions={
        <>
          <p>
            Click <strong>Open Paylocity</strong> to continue in a new window
            (make sure pop-ups are allowed). There, a Paylocity company
            administrator needs to:
          </p>
          <p>
            1. Sign in and open the <strong>Marketplace</strong> from the main
            menu.
            <br />
            2. Find <strong>{marketplaceListingName(client.name)}</strong> and
            click <strong>Create Request</strong>.
            <br />
            3. Select the companies to share and submit the request.
          </p>
          <p>
            We&apos;ll finish connecting here once the request is submitted.
          </p>
        </>
      }
    >
      <div
        style={{
          marginTop: 'auto',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <LoaderButton
          isLoading={loading || buttonLoading}
          disabled={loading || buttonLoading}
          onClick={onMarketplaceClick}
          text="Open Paylocity"
        />
      </div>
    </SignInBase>
  );
};

export default withAuthorizeContext(SignInPaylocityOAuthBase);
